/**
 * Quy chuẩn thiết kế Trang HTML (/trang/:slug).
 *
 * Để 1 chỗ duy nhất, vừa hiện trong admin vừa copy nguyên khối gửi cho người
 * code trang (hoặc dán vào ChatGPT/Claude làm brief). Ràng buộc ở đây không
 * phải "cho đẹp" mà là điều kiện để trang chạy được trong WebView của app:
 * trang nằm trong iframe sandbox (không same-origin), không header/footer web.
 */

export const DESIGN_RULES = [
    {
        title: "1. Khuôn khổ trang",
        items: [
            "Mobile-first: thiết kế cho khung 390×844, nội dung bọc trong khối max-width 640px, padding ngang 20px.",
            "100vh/100dvh DÙNG ĐƯỢC (khung nhúng cao đúng bằng màn hình), nhưng ưu tiên min-height thay vì height cố định để nội dung dài không bị cắt.",
            "position:fixed/sticky chạy được nhưng trên iOS hay giật khi cuộn — dùng thì phải test trong app, còn không thì đặt CTA thẳng trong luồng và lặp lại 2–3 lần.",
            "Không để tràn ngang: mọi khối `max-width:100%`, không `white-space:nowrap` cho đoạn dài.",
            "Trang được nhúng full màn hình, KHÔNG có header/footer của web — tự lo phần đầu trang (logo/tiêu đề) nếu cần.",
        ],
    },
    {
        title: "2. Cấu trúc file",
        items: [
            "Giao đúng 1 file .html tự chứa: <!DOCTYPE html> + <meta charset=\"UTF-8\"> + <meta name=\"viewport\" content=\"width=device-width, initial-scale=1.0\"> + toàn bộ CSS trong 1 thẻ <style> ở <head>. Dán một mẩu HTML rời cũng được — hệ thống tự bọc thêm charset + viewport.",
            "KHÔNG file .css/.js rời trong dự án, không build tool. Tải từ CDN ngoài (Google Fonts, thư viện JS) thì chạy được nhưng phụ thuộc mạng — ưu tiên nhúng thẳng.",
            "<script> chạy được, NHƯNG trang nằm ở origin ẩn danh: đụng localStorage / sessionStorage / cookie / document.domain là THROW, một lỗi JS chưa bắt đủ làm trắng cả trang.",
            "Có JS thì: chỉ thao tác DOM, bọc try/catch, không phụ thuộc dữ liệu đăng nhập (trang không biết user là ai).",
            "Nhúng iframe của bên thứ ba (YouTube, Google Maps, form) hay bị chặn vì thiếu same-origin — thay bằng ảnh thumbnail + link mở ngoài.",
            "Tổng file HTML nên < 300KB (giới hạn cứng khi lưu là 5MB). Ảnh base64 vẫn hiển thị được nhưng làm trang nặng gấp 30 lần, mở trong app rất chậm.",
        ],
    },
    {
        title: "3. Ảnh",
        items: [
            "Chỉ dùng URL tuyệt đối https://api.longhousee.com/img/pages/... — lấy bằng nút \"Tải ảnh & copy URL\" trong form. Path tương đối (./img/a.png) sẽ hỏng vì trang không nằm cùng thư mục với file gốc.",
            "KHÔNG nhúng base64 (data:image/...) — 3 ảnh base64 đã đẩy 1 trang từ 21KB lên 640KB, mở trong app chậm thấy rõ.",
            "Định dạng .webp, bề ngang tối đa 1080px, mỗi ảnh ≤ 150KB.",
            "Mọi <img> phải có width/height (hoặc aspect-ratio trong CSS) để trang không giật khi ảnh về, và có alt.",
            "CSS nền: img{max-width:100%; display:block;}",
        ],
    },
    {
        title: "4. Link",
        items: [
            "Link ra ngoài (Shopee/Lazada/TikTok/YouTube/Zalo): target=\"_blank\" rel=\"noopener\" — app tự đẩy sang trình duyệt/app ngoài để giữ tracking.",
            "Link về trang nhà mình dùng https://longhousee.com/... (giữ nguyên trong WebView, không văng ra ngoài).",
            "KHÔNG dùng deep link riêng (scheme lạ), không javascript: href, không window.open.",
            "Không làm form đăng nhập / nhập số điện thoại / nhập thông tin cá nhân: trang chạy ở origin ẩn danh, không có phiên đăng nhập.",
        ],
    },
    {
        title: "5. Chữ & màu (bộ nhận diện Longhousee)",
        items: [
            "Font: 'Be Vietnam Pro' cho thân bài, 'Baloo 2' cho tiêu đề, nạp qua Google Fonts kèm &display=swap, luôn khai báo fallback sans-serif.",
            "Cỡ chữ thân ≥ 15px, line-height ≥ 1.6; h1 28–34px; không chữ nào < 12.5px.",
            "Màu: cam chủ đạo #EE4D2D và #FF7A45, cam đậm #C4321A, nền #FFFAF6, giấy #FFFFFF, chữ #2B1B14, chữ phụ #7A6A5F, viền #F3E4D8, xanh xác nhận #1C9A5B, vàng nhấn #F5A623.",
            "Nút CTA: gradient cam, chữ trắng in đậm, bo tròn 999px, cao ≥ 48px, vùng chạm ≥ 44px.",
            "Bo góc khối 14–20px, đổ bóng nhẹ (vd 0 14px 30px -18px rgba(180,60,25,.28)), không viền đen đậm.",
        ],
    },
    {
        title: "6. Nội dung",
        items: [
            "Mở đầu 1 câu nêu thẳng lợi ích cho người đọc, không lời chào dài.",
            "Chia 3–5 bước đánh số, mỗi bước 1 câu ngắn + 1 ảnh chụp màn hình thật nếu có.",
            "Người xem đang ở TRONG app — không viết \"vào website\", \"bấm vào đây trên máy tính\".",
            "Kết trang bằng CTA + kênh hỗ trợ (Zalo). Video thì để ảnh thumbnail + link, không autoplay, không nhúng player.",
        ],
    },
    {
        title: "7. Nghiệm thu trước khi giao",
        items: [
            "Mở file bằng Chrome DevTools chế độ 390×844: cuộn mượt, không tràn ngang, chữ không dính mép.",
            "Tìm trong file, phải KHÔNG có: `src=\"./`, `src=\"/`, `data:image`, `http://` (thiếu s).",
            "Tắt mạng thử: font lỗi vẫn phải đọc được (nhờ fallback).",
            "Gửi kèm bộ ảnh gốc .webp để admin upload lại đúng đường dẫn api.longhousee.com.",
        ],
    },
    {
        title: "8. Bối cảnh kỹ thuật (đừng đổi, chỉ để biết)",
        items: [
            "Trang hiện full màn hình tại https://longhousee.com/trang/<slug>; nội dung được nạp từ https://api.longhousee.com/api/v1/page/raw/<slug> và nhúng bằng iframe.",
            "Document đó chạy ở origin ẩn danh (header CSP sandbox, không allow-same-origin) → không đọc được cookie/localStorage, nhưng vẫn chạy JS, form, mở tab mới.",
            "Trong app, URL cha có thêm ?lh_view=app nhưng bên trong không đọc được — đừng thiết kế phụ thuộc tham số này.",
            "Trang được mở ở 3 nơi: trình duyệt web, WebView nhúng trong app (appview) và trình duyệt trong app (webview). Cả 3 phải hiện giống nhau — test đủ 3 trước khi chốt.",
            "Nội dung được cache 60 giây: sửa xong xem lại mà thấy bản cũ thì đợi 1 phút hoặc thêm ?v=2 vào URL.",
        ],
    },
];

/** Bản text thuần để copy 1 phát gửi cho người code / dán vào AI. */
export const DESIGN_RULES_TEXT = [
    "QUY CHUẨN THIẾT KẾ TRANG HTML — LONGHOUSEE (/trang/:slug)",
    "Trang sẽ được dán nguyên văn vào admin và hiển thị full màn hình trong app qua iframe sandbox.",
    "",
    ...DESIGN_RULES.map(
        (section) => `${section.title}\n${section.items.map((item) => `- ${item}`).join("\n")}`
    ),
].join("\n\n");

export default DESIGN_RULES;
